import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../../../providers/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getDataWidget } from '../../../../../services/apiWidgests';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Sector } from 'recharts';
import Spinner from '../../../../../components/Spinner';

const generatePastelColor = (index) => {
  const hue = (index * 360) / 10;
  return `hsl(${hue}, 70%, 80%)`;
};

const renderActiveShape = (props) => {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill
  } = props;

  return (
    <g>
      <Sector
        className="transform 0.2s ease-out"
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 10}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
};


const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const { name, value, payload: item } = payload[0];
    const percentage = ((value / item.total) * 100).toFixed(1);
    return (
      <div className="bg-black text-white px-2 py-1 rounded opacity-90 flex items-center">
        <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: item.color }}></div>
        <span>{name}: {value} ({percentage}%)</span>
      </div>
    );
  }
  return null;
};

const PieChartWidget = ({ id }) => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [fetchingWidgetData, setFetchingWidgetData] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);

  const refreshWidgetData = async () => {
    try {
      setFetchingWidgetData(true);
      const response = await getDataWidget(id, {}, auth?.token);
      const records = response.records || [];
      const total = records.reduce((sum, r) => sum + r.value, 0);
      const enrichedData = records.map((d, i) => ({
        ...d,
        total,
        color: generatePastelColor(i)
      }));
      setData(enrichedData);
      setTotalValue(total);
      setFetchingWidgetData(false);
    } catch (error) {
      if (error.response?.status === 404) {
        console.log("Widget no encontrado");
      } else if (error.response?.status === 500) {
        console.log("Error en el servidor, intente nuevamente");
      } else {
        navigate('/logout');
      }
      setFetchingWidgetData(false);
    }
  };

  useEffect(() => {
    refreshWidgetData();
  }, []);

  return (
    <div className="relative w-full h-full pb-20">
      {fetchingWidgetData ? (
        <Spinner />
      ) : (
        <>
          <div className="p-2 bg-white border border-gray-300 flex flex-wrap">
            {data.map((item, index) => {
              const percentage = ((item.value / totalValue) * 100).toFixed(1);
              return (
                <div key={item.label} className="flex items-center mb-2 mr-4">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="ml-2">{item.label}: {percentage}%</span>
                </div>
              );
            })}
          </div>

          <div className="h-[90%] mt-[1%]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius="40%"
                  outerRadius="80%"
                  paddingAngle={2}
                  isAnimationActive
                  activeIndex={activeIndex}
                  activeShape={renderActiveShape}
                  onMouseEnter={(_, index) => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  label={({ value, payload }) => {
                    const percentage = ((value / payload.total) * 100).toFixed(1);
                    return `${percentage}%`;
                  }}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default PieChartWidget;
